import { useEffect, useState, useRef } from 'react';
import { SQLiteService } from '../services/SQLiteService';

/**
 * Hook to provide access to the SQLite service
 * Creates a separate instance per document to avoid conflicts
 * @param documentId - Optional document ID to initialize a specific database
 */
export const useSQLiteService = (documentId?: string) => {
  const [isInitialized, setIsInitialized] = useState(false);
  const serviceRef = useRef<SQLiteService | null>(null);

  useEffect(() => {
    let cancelled = false;
    
    const initializeService = async () => {
      
      try {
        // First, always clean up any existing service
        if (serviceRef.current) {
          await serviceRef.current.close();
          serviceRef.current = null;
          setIsInitialized(false);
        }
        
        // Only initialize if documentId is provided and effect not cancelled
        if (!documentId) {
          return;
        }
        
        if (cancelled) {
          return;
        }

        const dbName = `${documentId}.db`;
        
        const newService = new SQLiteService();
        await newService.initialize(dbName);
        
        // Check if effect was cancelled during async initialization
        if (cancelled) {
          await newService.close();
          return;
        }
        
        serviceRef.current = newService;
        setIsInitialized(true);
        
      } catch (error) {
        if (!cancelled) {
          console.error(`Failed to initialize SQLite service for ${documentId}:`, error);
          setIsInitialized(false);
          serviceRef.current = null;
        }
      }
    };

    initializeService();
    
    // Cleanup when component unmounts or documentId changes
    return () => {
      cancelled = true;
      if (serviceRef.current) {
        // Don't await here as cleanup functions can't be async
        serviceRef.current.close().catch(err => 
          console.error('Error closing SQLite service during cleanup:', err)
        );
        serviceRef.current = null;
        setIsInitialized(false);
      }
    };
  }, [documentId]);

  return isInitialized ? serviceRef.current : null;
};
